import { useEffect, useRef, useState } from "react";
import type { ChatSession } from "../../api/agent";
import styles from "../../pages/Agent/AgentDashboard.module.css";
export default function AgentHistory({
  sessions,
  current,
  onSelect,
  onRename,
  onDelete,
}: {
  sessions: ChatSession[];
  current: string | null;
  onSelect: (id: string) => void;
  onRename?: (id: string, title: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [search, setSearch] = useState("");
  const [edit, setEdit] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [confirm, setConfirm] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionsFor, setActionsFor] = useState<string | null>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    actionsRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [actionsFor]);
  const action = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      setEdit(null);
      setConfirm(null);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "The change could not be saved. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };
  const groups = new Map<string, ChatSession[]>();
  for (const session of sessions.filter((s) =>
    (s.title || "Untitled chat").toLowerCase().includes(search.toLowerCase()),
  )) {
    const age =
      (Date.now() - Date.parse(session.updated_at || session.created_at)) /
      86400000;
    const group =
      age < 1
        ? "Today"
        : age < 7
          ? "Previous 7 days"
          : age < 30
            ? "Previous 30 days"
            : "Earlier";
    groups.set(group, [...(groups.get(group) || []), session]);
  }
  return (
    <div className={styles.history}>
      <label>
        Search conversations
        <input
          type="search"
          placeholder="Search by title"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </label>
      {error && <p role="alert">{error}</p>}
      {groups.size === 0 && <p>No conversations found.</p>}
      {[...groups].map(([group, items]) => (
        <section key={group}>
          <h3>{group}</h3>
          {items.map((s) => (
            <article key={s.session_id} className={styles.historyItem}>
              <button
                className={styles.historySelect}
                aria-current={current === s.session_id ? "true" : undefined}
                onClick={() => onSelect(s.session_id)}
              >
                <strong>{s.title || "Untitled chat"}</strong>
                <time dateTime={s.created_at}>
                  {new Date(s.created_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </time>
              </button>
              <button
                type="button"
                className={styles.historyActionsToggle}
                aria-label={`Actions for ${s.title || "Untitled chat"}`}
                aria-expanded={actionsFor === s.session_id}
                aria-controls={`history-actions-${s.session_id}`}
                onClick={() =>
                  setActionsFor(
                    actionsFor === s.session_id ? null : s.session_id,
                  )
                }
              >
                •••
              </button>
              {actionsFor === s.session_id && (
                <div
                  id={`history-actions-${s.session_id}`}
                  ref={actionsRef}
                  className={styles.actions}
                >
                  {onRename && (
                    <button
                      onClick={() => {
                        setEdit(s.session_id);
                        setTitle(s.title || "");
                      }}
                    >
                      Rename
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setConfirm(s.session_id);
                      setError("");
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
              {edit === s.session_id && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (title.trim() && onRename)
                      void action(() => onRename(s.session_id, title.trim()));
                  }}
                >
                  <label>
                    Conversation title
                    <input
                      value={title}
                      maxLength={120}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </label>
                  <button disabled={busy || !title.trim()}>Save title</button>
                  <button type="button" onClick={() => setEdit(null)}>
                    Cancel rename
                  </button>
                </form>
              )}
              {confirm === s.session_id && (
                <div role="group" aria-label="Confirm deletion">
                  <p>
                    Delete “{s.title || "Untitled chat"}”? This removes the
                    conversation history.
                  </p>
                  <button
                    disabled={busy}
                    onClick={() => void action(() => onDelete(s.session_id))}
                  >
                    Delete conversation
                  </button>
                  <button disabled={busy} onClick={() => setConfirm(null)}>
                    Keep conversation
                  </button>
                </div>
              )}
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}
